import * as XLSX from 'xlsx';

export type ExportFormat = 'csv' | 'excel';

export interface ExportColumn {
  field: string;
  headerName: string;
  width?: number;
}

export interface ExportOptions {
  filename?: string;
  sheetName?: string;
  includeHeaders?: boolean;
}

/**
 * Export data to CSV format
 */
export const exportToCSV = (
  data: any[],
  columns: ExportColumn[],
  options: ExportOptions = {}
): void => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const { filename = 'export' } = options;

  // Create headers row
  const headers = columns.map((col) => col.headerName);

  // Create data rows
  const rows = data.map((row) =>
    columns.map((col) => {
      const value = row[col.field];
      // Handle null/undefined values
      if (value === null || value === undefined) return '';
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const stringValue = String(value);
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    })
  );

  // Combine headers and data
  const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data to Excel format
 */
export const exportToExcel = (
  data: any[],
  columns: ExportColumn[],
  options: ExportOptions = {}
): void => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const { filename = 'export', sheetName = 'Sheet1' } = options;

  // Prepare data for Excel with correct column order
  const excelData = data.map((row) => {
    const excelRow: any = {};
    columns.forEach((col) => {
      const value = row[col.field];
      excelRow[col.headerName] =
        value === null || value === undefined ? '' : value;
    });
    return excelRow;
  });

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();

  // Create worksheet manually to preserve column order
  const worksheet = XLSX.utils.aoa_to_sheet([
    // Header row with correct column order
    columns.map((col) => col.headerName),
    // Data rows with correct column order
    ...excelData.map((row) => columns.map((col) => row[col.headerName])),
  ]);

  // Set column widths if provided
  if (columns.some((col) => col.width)) {
    const colWidths = columns.map((col) => {
      // Convert width to Excel column width (roughly 1 character = 1 unit)
      // MUI DataGrid uses pixels, Excel uses character units
      const excelWidth = Math.max(10, Math.min(50, (col.width || 150) / 8));
      return { wch: excelWidth };
    });
    worksheet['!cols'] = colWidths;
  }

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = `${filename}_${timestamp}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, finalFilename);
};

/**
 * Get current data from DataGrid (handles pagination, filtering, sorting)
 */
export const getCurrentDataFromDataGrid = (
  dataGridRef: any,
  allData: any[],
  columns: ExportColumn[]
): { data: any[]; columns: ExportColumn[] } => {
  if (!dataGridRef?.current) {
    return { data: allData, columns };
  }

  const apiRef = dataGridRef.current;

  // Get current page data (currently unused but kept for future DataGrid integration)
  // const currentPage = apiRef.state.pagination.paginationModel.page;
  // const pageSize = apiRef.state.pagination.paginationModel.pageSize;

  // Get filtered and sorted data
  let processedData = [...allData];

  // Apply filters if any
  const filterModel = apiRef.state.filter.filterModel;
  if (filterModel && Object.keys(filterModel).length > 0) {
    processedData = processedData.filter((row) => {
      return Object.entries(filterModel).every(([field, filter]) => {
        if (!filter) return true;
        const value = row[field];
        const filterValue = (filter as any).value;

        if ((filter as any).operator === 'contains') {
          return String(value)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        }
        if ((filter as any).operator === 'equals') {
          return value === filterValue;
        }
        if ((filter as any).operator === 'startsWith') {
          return String(value)
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        }
        if ((filter as any).operator === 'endsWith') {
          return String(value)
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        }
        return true;
      });
    });
  }

  // Apply sorting if any
  const sortModel = apiRef.state.sorting.sortModel;
  if (sortModel && sortModel.length > 0) {
    processedData.sort((a, b) => {
      for (const sort of sortModel) {
        const aValue = a[sort.field];
        const bValue = b[sort.field];

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        if (comparison !== 0) {
          return sort.sort === 'desc' ? -comparison : comparison;
        }
      }
      return 0;
    });
  }

  // Return all data or current page based on preference
  // For now, return all filtered/sorted data
  return { data: processedData, columns };
};

/**
 * Format data for export (handles special data types)
 */
export const formatDataForExport = (
  data: any[],
  columns: ExportColumn[]
): any[] => {
  return data.map((row) => {
    const formattedRow: any = {};
    columns.forEach((col) => {
      let value = row[col.field];

      // Handle different data types
      if (value === null || value === undefined) {
        formattedRow[col.field] = '';
      } else if (value instanceof Date) {
        formattedRow[col.field] = value.toLocaleDateString();
      } else if (typeof value === 'boolean') {
        formattedRow[col.field] = value ? 'Yes' : 'No';
      } else if (typeof value === 'object') {
        formattedRow[col.field] = JSON.stringify(value);
      } else {
        formattedRow[col.field] = String(value);
      }
    });
    return formattedRow;
  });
};
